import { inRow, onLine, tiles } from "./fuxi-tile-moves.js";
import { type Drag, drop, showMark } from "./tile-landing.js";
import { at, ghostOf, moveGhost } from "./tile-drop.js";

export { dropIndex } from "./tile-drop.js";

/**
 * Dragging a word-order tile to the place on the line it belongs, with a
 * mouse or a finger.
 *
 * A tap puts a tile at the end of the line, which leaves a reader who has
 * missed a word taking tiles back to make room. Hugh asked on 2026-10-15 for
 * what Duolingo's word bank does: drag a tile from the row and drop it between
 * two on the line, or drag one along the line. A tile on the line dropped off
 * it goes back to the row. A press that never moves far is still a tap, and
 * the keys are untouched.
 *
 * Pointer events throughout, so one path serves a mouse, a pen and a finger.
 * The tiles take `touch-action: none` (\`modules/lianxi\`), or a finger
 * dragging one would scroll the page instead.
 */

/** How far a press moves, in pixels, before it is a drag and not a tap. */
const dragDistance = 6;

/**
 * Let the tiles in a block be dragged.
 * @param block - What holds the tiles: an exercise, or one card.
 */
export function dragTiles(block: HTMLElement): void {
  let drag: Drag | undefined;
  let dragged = false;

  block.addEventListener("pointerdown", (event: PointerEvent) => {
    const tile = (event.target as Element).closest<HTMLElement>(
      `${inRow}:not([disabled]), ${onLine}`,
    );

    dragged = false;

    if (tile === null || event.button !== 0) {
      return;
    }

    drag = { tile, card: tile.closest(tiles)!, start: at(event) };
    tile.setPointerCapture?.(event.pointerId);
  });

  block.addEventListener("pointermove", (event: PointerEvent) => {
    const point = at(event);

    if (drag === undefined) {
      return;
    }

    if (
      drag.ghost === undefined &&
      Math.hypot(point.x - drag.start.x, point.y - drag.start.y) < dragDistance
    ) {
      return;
    }

    drag.ghost ??= ghostOf(drag.tile, point);
    drag.tile.dataset["cbFuxiDragging"] = "";
    moveGhost(drag.ghost, point);
    showMark(drag, point);
  });

  const end = (event: PointerEvent, landing: boolean): void => {
    if (drag?.ghost !== undefined) {
      if (landing) {
        drop(drag, at(event));
      }

      drag.ghost.remove();
      drag.mark?.remove();
      delete drag.tile.dataset["cbFuxiDragging"];
      dragged = true;
      // The click a drag ends in comes in the same turn as the pointer going
      // up, so a click after this turn is a press of its own
      setTimeout(() => {
        dragged = false;
      }, 0);
    }

    drag = undefined;
  };

  block.addEventListener("pointerup", (event: PointerEvent) => {
    end(event, true);
  });
  block.addEventListener("pointercancel", (event: PointerEvent) => {
    end(event, false);
  });

  // The press a drag ends in is not a tap on the tile it ended on
  block.addEventListener(
    "click",
    (event: Event) => {
      if (
        dragged &&
        (event.target as Element).closest(`${inRow}, ${onLine}`) !== null
      ) {
        dragged = false;
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    { capture: true },
  );
}
